import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Clock, PencilSimple, Plus, Copy, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import { useUser } from "@clerk/clerk-react";
import CommentsThread from "@/components/CommentsThread";
import { hasUnread } from "@/lib/commentReads";
import { stripMd, renderRich } from "@/lib/richText";
import { FullPreviewModal } from "@/components/FullPreview/FullPreviewModal";

const copyPlain = async (text) => {
  try { await navigator.clipboard.writeText(stripMd(text)); toast.success("Copied — plain text, no highlight"); }
  catch { toast.error("Couldn't copy — try selecting the text manually"); }
};

const PLATFORMS = ["instagram", "facebook", "linkedin", "tiktok"];
const platformColors = { facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2", tiktok: "#010101" };

const statusBadge = (status) =>
  status === "approved" ? "bg-[#e7f6ec] text-[#15803d]" :
  status === "pending_review" ? "bg-[#fef7e0] text-[#a16207]" :
  "bg-[#eef1f2] text-[#3C4048]";

function CompositionOverlay({ imageUrl, overlay, className = "" }) {
  const teal = overlay.theme === "teal";
  return (
    <div className={`relative overflow-hidden rounded-md ${className}`} data-testid="composition-overlay">
      <img src={imageUrl} alt="composition" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-x-0 bottom-0" style={{ height: "42%", background: teal ? "#5FA8B4" : "rgba(255,255,255,0.94)" }} />
      <div className="relative h-full flex flex-col justify-end p-5">
        {!teal && <div className="w-10 h-[3px] mb-2" style={{ backgroundColor: "#5FA8B4" }} />}
        <h3 className="font-bold leading-tight" style={{ fontFamily: "Montserrat", fontSize: "clamp(1.1rem, 3vw, 1.9rem)", letterSpacing: "0.01em", color: teal ? "#FFFFFF" : "#3C4048" }}>
          {overlay.headline}
        </h3>
        {overlay.subtext && (
          <p className="text-sm mt-1" style={{ fontFamily: "Montserrat", color: teal ? "rgba(255,255,255,0.92)" : "#7A9090" }}>{overlay.subtext}</p>
        )}
        {overlay.logo_url && <img src={overlay.logo_url} alt="brand logo" className="h-7 w-auto mt-3 self-start" />}
      </div>
    </div>
  );
}

export default function Calendar() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState({});
  const { canManageContent, canApprove, canRequestRevision, canDelete } = useRole();
  const { user } = useUser();
  const [members, setMembers] = useState([]);
  const emptyForm = { draft_text: "", platform: "instagram", scheduled_date: "", attached_media_id: "", visibility: "company", assigned_to: [] };
  const [createOpen, setCreateOpen] = useState(false);
  const [cForm, setCForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineFeedback, setDeclineFeedback] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [fullPreview, setFullPreview] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchAllMedia();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (location.state?.attachMediaId) {
      setCForm((f) => ({ ...f, attached_media_id: location.state.attachMediaId }));
      setCreateOpen(true);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  useEffect(() => {
    if (selectedPost) setEditedText(selectedPost.final_text || selectedPost.draft_text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPost]);

  const fetchPosts = async () => {
    try { const response = await api.get(`/posts`); setPosts(response.data); }
    catch { toast.error("Failed to fetch posts"); }
    finally { setLoading(false); }
  };

  const fetchAllMedia = async () => {
    try { const response = await api.get(`/media`); const map = {}; response.data.forEach((m) => { map[m.id] = m; }); setMedia(map); }
    catch (error) { console.error("Failed to fetch media", error); }
  };

  const fetchMembers = async () => {
    try { const res = await api.get(`/members`); setMembers(res.data.filter((m) => m.user_id)); }
    catch (error) { console.error("Failed to fetch members", error); }
  };

  const handleApprove = async () => {
    try {
      if (isEditing && editedText !== selectedPost.draft_text) {
        await api.put(`/posts/${selectedPost.id}`, { final_text: editedText, status: "approved" });
      } else {
        await api.post(`/posts/${selectedPost.id}/approve`);
      }
      toast.success("Post approved successfully");
      setSelectedPost(null); setIsEditing(false); fetchPosts();
    } catch { toast.error("Failed to approve post"); }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/posts/${selectedPost.id}`, { final_text: editedText });
      toast.success("Changes saved");
      setIsEditing(false); setSelectedPost(null); fetchPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const confirmDecline = async () => {
    try {
      await api.post(`/posts/${selectedPost.id}/decline`, { feedback: declineFeedback || null });
      toast.success("Sent back for revision");
      setDeclineOpen(false); setDeclineFeedback(""); setSelectedPost(null); fetchPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/posts/${selectedPost.id}`);
      toast.success("Post deleted");
      setDeleteOpen(false); setSelectedPost(null); fetchPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const handleCreate = async () => {
    if (!cForm.draft_text.trim() || !cForm.scheduled_date) { toast.error("Add text and a scheduled date"); return; }
    setCreating(true);
    try {
      await api.post(`/posts`, {
        draft_text: cForm.draft_text,
        platform: cForm.platform,
        scheduled_date: new Date(cForm.scheduled_date).toISOString(),
        attached_media_id: cForm.attached_media_id || null,
        visibility: cForm.visibility,
        assigned_to: cForm.visibility === "assigned" ? cForm.assigned_to : [],
      });
      toast.success("Post created");
      setCreateOpen(false); setCForm(emptyForm); fetchPosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const calculateTimeRemaining = (autoApproveAt) => {
    if (!autoApproveAt) return "N/A";
    const diff = new Date(autoApproveAt) - new Date();
    if (diff <= 0) return "Processing…";
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const groupPostsByDate = () => {
    const grouped = {};
    posts.forEach((post) => {
      const date = new Date(post.scheduled_date).toISOString().split("T")[0];
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(post);
    });
    return Object.entries(grouped).sort((a, b) => new Date(a[0]) - new Date(b[0]));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="text-[#3C4048] text-xl">Loading calendar…</div></div>;
  }

  const groupedPosts = groupPostsByDate();

  return (
    <div className="space-y-8" data-testid="calendar-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Content Calendar</h1>
          <p className="text-[#7A9090]">Review and approve scheduled content</p>
        </div>
        {canManageContent && (
          <Button data-testid="new-post-button" onClick={() => setCreateOpen(true)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px]">
            <Plus size={18} className="mr-2" weight="bold" /> New Post
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {groupedPosts.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-12 text-center">
            <p className="text-[#7A9090]">No posts scheduled yet</p>
          </div>
        ) : (
          groupedPosts.map(([date, datePosts]) => (
            <div key={date} className="space-y-3">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
                  {new Date(date).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </h2>
                <span className="text-[#7A9090] text-sm">{datePosts.length} posts</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {datePosts.map((post) => (
                  <div key={post.id} data-testid={`calendar-post-${post.id}`} onClick={() => setSelectedPost(post)}
                    className="bg-white border border-[#e5e7eb] rounded-[12px] p-4 hover:border-[#5FA8B4] cursor-pointer transition-colors duration-200">
                    <div className="flex items-start justify-between mb-3">
                      <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                      <div className="flex items-center gap-1.5">
                        {hasUnread(post) && <span data-testid={`unread-${post.id}`} className="w-2 h-2 rounded-full bg-[#ef4444]" title="New comments" />}
                        {post.comment_count > 0 && <span className="text-[#7A9090] text-[11px]">💬 {post.comment_count}</span>}
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${statusBadge(post.status)}`}>{post.status.replace("_", " ")}</span>
                      </div>
                    </div>

                    {post.overlay && media[post.attached_media_id] && media[post.attached_media_id].kind !== "video" && (
                      <CompositionOverlay imageUrl={media[post.attached_media_id].file_url} overlay={post.overlay} className="aspect-[16/9] mb-3" />
                    )}

                    <p className="text-[#3C4048] text-sm line-clamp-3 mb-3 whitespace-pre-line">{renderRich(post.draft_text)}</p>

                    {post.status === "pending_review" && post.auto_approve_at && (
                      <div className="flex items-center gap-2 text-[#a16207] text-xs font-medium">
                        <Clock size={14} weight="regular" />
                        <span>Auto-approve in {calculateTimeRemaining(post.auto_approve_at)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Review dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => { setSelectedPost(null); setIsEditing(false); }}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#3C4048] max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Review Post</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded text-white text-sm font-semibold" style={{ backgroundColor: platformColors[selectedPost.platform] || "#7A9090" }}>{selectedPost.platform}</span>
                  <span className="text-[#7A9090] text-sm">Scheduled: {new Date(selectedPost.scheduled_date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                </div>

                {selectedPost.status === "pending_review" && selectedPost.auto_approve_at && (
                  <div className="bg-[#fffdf5] border border-[#eab308] rounded-[12px] p-4">
                    <div className="flex items-center gap-2 text-[#a16207]">
                      <Clock size={20} weight="duotone" />
                      <span className="font-bold" style={{ fontFamily: "Montserrat" }}>Silence is Approval Timer</span>
                    </div>
                    <p className="text-[#3C4048] mt-2 text-xl">Auto-approve in: {calculateTimeRemaining(selectedPost.auto_approve_at)}</p>
                  </div>
                )}

                {selectedPost.attached_media_id && media[selectedPost.attached_media_id] ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[#7A9090] text-xs uppercase tracking-wider block">{selectedPost.overlay ? "Composition Preview" : "Attached Media"}</label>
                      <Button data-testid="calendar-full-preview-btn" size="sm" variant="outline"
                        onClick={() => setFullPreview({ type: "social", data: {
                          platform: selectedPost.platform, mediaUrl: media[selectedPost.attached_media_id].file_url,
                          mediaKind: media[selectedPost.attached_media_id].kind, mediaId: selectedPost.attached_media_id,
                          caption: selectedPost.final_text || selectedPost.draft_text,
                        } })}
                        className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-7 text-xs">
                        <Eye size={13} /> Full Preview
                      </Button>
                    </div>
                    {selectedPost.overlay && media[selectedPost.attached_media_id].kind !== "video" ? (
                      <CompositionOverlay imageUrl={media[selectedPost.attached_media_id].file_url} overlay={selectedPost.overlay} className="aspect-square max-w-md border border-[#e5e7eb]" />
                    ) : media[selectedPost.attached_media_id].kind === "video" ? (
                      <video src={media[selectedPost.attached_media_id].file_url} controls className="w-full rounded-[12px] border border-[#e5e7eb]" data-testid="attached-media-video" />
                    ) : (
                      <img src={media[selectedPost.attached_media_id].file_url} alt="Post media" className="w-full rounded-[12px] border border-[#e5e7eb]" />
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {media[selectedPost.attached_media_id].tags.map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-[#eef1f2] text-[#3A7A8C] text-xs rounded">{tag}</span>
                      ))}
                    </div>
                  </div>
                ) : selectedPost.attached_media_url && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[#7A9090] text-xs uppercase tracking-wider block">Attached Media</label>
                      <Button data-testid="calendar-full-preview-btn" size="sm" variant="outline"
                        onClick={() => setFullPreview({ type: "social", data: {
                          platform: selectedPost.platform, mediaUrl: selectedPost.attached_media_url,
                          mediaKind: selectedPost.attached_media_kind, caption: selectedPost.final_text || selectedPost.draft_text,
                        } })}
                        className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-7 text-xs">
                        <Eye size={13} /> Full Preview
                      </Button>
                    </div>
                    {selectedPost.attached_media_kind === "video" ? (
                      <video src={selectedPost.attached_media_url} controls className="w-full rounded-[12px] border border-[#e5e7eb]" data-testid="attached-media-video" />
                    ) : (
                      <img src={selectedPost.attached_media_url} alt="Post media" className="w-full rounded-[12px] border border-[#e5e7eb]" />
                    )}
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[#7A9090] text-xs uppercase tracking-wider">Post Content</label>
                    {!isEditing && canManageContent && (
                      <Button data-testid="edit-post-button" onClick={() => setIsEditing(true)} size="sm" className="bg-[#f0f7f8] border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#5FA8B4] hover:text-white h-7 text-xs rounded-[10px]">
                        <PencilSimple size={14} className="mr-1" weight="regular" /> Edit
                      </Button>
                    )}
                  </div>
                  <div className="relative group bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] p-4 text-[#3C4048] whitespace-pre-line">
                    <button onClick={() => copyPlain(selectedPost.draft_text)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-[#7A9090] hover:text-[#3A7A8C] transition-opacity" title="Copy plain text" aria-label="Copy caption as plain text" data-testid="copy-draft-text">
                      <Copy size={14} />
                    </button>
                    {renderRich(selectedPost.draft_text)}
                  </div>
                </div>

                {isEditing && (
                  <div>
                    <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Edit Final Text</label>
                    <Textarea data-testid="edit-post-textarea" value={editedText} onChange={(e) => setEditedText(e.target.value)} className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[150px] whitespace-pre-line" />
                    {editedText !== selectedPost.draft_text && <p className="text-[#a16207] text-xs mt-2">Changes detected — will be tracked for learning</p>}
                    {canManageContent && (
                      <Button data-testid="save-edit-button" onClick={handleSaveEdit} className="mt-3 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-8 text-xs rounded-[10px]">Save Changes</Button>
                    )}
                  </div>
                )}

                {selectedPost.final_text && selectedPost.final_text !== selectedPost.draft_text && !isEditing && (
                  <div>
                    <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Final Approved Text</label>
                    <div className="relative group bg-[#f0faf3] border border-[#22c55e] rounded-[12px] p-4 text-[#3C4048] whitespace-pre-line">
                      <button onClick={() => copyPlain(selectedPost.final_text)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-[#7A9090] hover:text-[#3A7A8C] transition-opacity" title="Copy plain text" aria-label="Copy final text as plain text" data-testid="copy-final-text">
                        <Copy size={14} />
                      </button>
                      {renderRich(selectedPost.final_text)}
                    </div>
                  </div>
                )}

                {selectedPost.feedback && (
                  <div className="bg-[#fffdf5] border border-[#eab308] rounded-[12px] p-3 text-sm" data-testid="post-feedback">
                    <span className="text-[#a16207] font-semibold">Feedback: </span>
                    <span className="text-[#3C4048] whitespace-pre-line">{selectedPost.feedback}</span>
                  </div>
                )}

                <div className="border-t border-[#eef1f2] pt-4">
                  <CommentsThread api={api} postId={selectedPost.id} currentName={user?.fullName || user?.firstName || "Team"} />
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  {canApprove && ["pending_review", "needs_revision"].includes(selectedPost.status) && (
                    <Button data-testid="approve-post-button" onClick={handleApprove} className="flex-1 min-w-[140px] bg-[#22c55e] text-white hover:bg-[#16a34a] rounded-[12px]">
                      <CheckCircle size={18} className="mr-2" weight="bold" /> Approve
                    </Button>
                  )}
                  {canRequestRevision && selectedPost.status !== "needs_revision" && (
                    <Button data-testid="request-revision-button" onClick={() => setDeclineOpen(true)} className="flex-1 min-w-[140px] bg-white border border-[#eab308] text-[#a16207] hover:bg-[#eab308] hover:text-white rounded-[12px]">
                      Request Revision
                    </Button>
                  )}
                  {canDelete && (
                    <Button data-testid="delete-post-button" onClick={() => setDeleteOpen(true)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white rounded-[12px]">Delete</Button>
                  )}
                  <Button data-testid="cancel-review-button" onClick={() => { setSelectedPost(null); setIsEditing(false); }} className="bg-white border border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] rounded-[12px]">Close</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decline / request revision dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#3C4048] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Request Revision</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#7A9090] -mt-2">Add a note for the team (optional). This sends the post back for changes.</p>
          <Textarea data-testid="decline-feedback" value={declineFeedback} onChange={(e) => setDeclineFeedback(e.target.value)} placeholder="What needs to change?" className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[100px]" />
          <div className="flex gap-3">
            <Button data-testid="decline-confirm" onClick={confirmDecline} className="flex-1 bg-[#eab308] text-white hover:bg-[#ca8a04] rounded-[12px]">Send back for revision</Button>
            <Button onClick={() => setDeclineOpen(false)} className="bg-white border border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] rounded-[12px]">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">This permanently removes the post. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="bg-[#ef4444] text-white hover:bg-[#dc2626]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create post dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#3C4048] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>New Post</DialogTitle>
            <DialogDescription className="sr-only">Create a new post with text, platform, schedule and an optional attached image</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Content</label>
              <Textarea data-testid="create-text" value={cForm.draft_text} onChange={(e) => setCForm((f) => ({ ...f, draft_text: e.target.value }))} placeholder={"Hook first…\n\nValue second…\n\nCTA third.\n\n#hashtags"} className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[140px] whitespace-pre-line" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Platform</label>
                <select data-testid="create-platform" value={cForm.platform} onChange={(e) => setCForm((f) => ({ ...f, platform: e.target.value }))} className="w-full bg-[#fafafa] border border-[#e5e7eb] text-[#3C4048] rounded-[10px] p-2 text-sm">
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Scheduled Date</label>
                <input data-testid="create-date" type="datetime-local" value={cForm.scheduled_date} onChange={(e) => setCForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="w-full bg-[#fafafa] border border-[#e5e7eb] text-[#3C4048] rounded-[10px] p-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Attach Image (from media library)</label>
              <select data-testid="create-media" value={cForm.attached_media_id} onChange={(e) => setCForm((f) => ({ ...f, attached_media_id: e.target.value }))} className="w-full bg-[#fafafa] border border-[#e5e7eb] text-[#3C4048] rounded-[10px] p-2 text-sm">
                <option value="">None</option>
                {Object.values(media).map((mm) => <option key={mm.id} value={mm.id}>{mm.filename}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Visibility</label>
              <div className="flex gap-2">
                {[{ k: "company", l: "Everyone (company)" }, { k: "assigned", l: "Assigned staff only" }].map((v) => (
                  <button key={v.k} type="button" data-testid={`visibility-${v.k}`} onClick={() => setCForm((f) => ({ ...f, visibility: v.k }))}
                    className={`flex-1 text-xs py-2 rounded-[10px] border ${cForm.visibility === v.k ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb]"}`}>{v.l}</button>
                ))}
              </div>
            </div>
            {cForm.visibility === "assigned" && (
              <div data-testid="assign-staff">
                <label className="text-[#7A9090] text-xs uppercase tracking-wider mb-2 block">Assign to</label>
                <div className="flex flex-wrap gap-2">
                  {members.length === 0 && <p className="text-[#a3b0b0] text-xs">No members found.</p>}
                  {members.map((m) => {
                    const on = cForm.assigned_to.includes(m.user_id);
                    return (
                      <button key={m.user_id} type="button" data-testid={`assign-${m.user_id}`}
                        onClick={() => setCForm((f) => ({ ...f, assigned_to: on ? f.assigned_to.filter((x) => x !== m.user_id) : [...f.assigned_to, m.user_id] }))}
                        className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb]"}`}>
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button data-testid="create-submit" onClick={handleCreate} disabled={creating} className="flex-1 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px]">{creating ? "Creating…" : "Create Post"}</Button>
              <Button onClick={() => setCreateOpen(false)} className="bg-white border border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] rounded-[12px]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FullPreviewModal open={!!fullPreview} onOpenChange={(v) => !v && setFullPreview(null)} preview={fullPreview} />
    </div>
  );
}
